import Base from '@studiometa/js-toolkit/abstracts/Base';
import transition from '@studiometa/js-toolkit/utils/css/transition';
import { isDev, useDrupal } from '../config';
import MenuMain from './MenuMain';
import MenuBurger from './MenuBurger';
import Search from './Search';
import MobileSearch from './MobileSearch';
import ProfilTopHeader from './ProfilTopHeader';

/**
 * Header class.
 */
export default class Header extends Base {
  /**
   * Class config.
   * @return {Object}
   */
  get config() {
    return {
      name: 'Header',
      components: {
        MenuMain,
        MenuBurger,
        Search,
        MobileSearch,
        ProfilTopHeader,
      },
      log: isDev(),
      styles: {
        active: 'transition-transform duration-300 ease-out-sine',
        hidden: 'transform -translate-y-full pointer-events-none',
      },
    };
  }

  /**
   * Update position when mouting the component.
   * @return {void}
   */
  mounted() {
    this.isVisible =
      window.pageYOffset <= this.$refs.header.offsetTop + this.$refs.header.offsetHeight;
    this.updatePosition();

    this.isLoaded = true;
    this.initToolbarBehaviour();
    this.updatePosition();
  }

  /**
   * Update header position when resizing.
   * @return {void}
   */
  resized() {
    this.updatePosition();
  }

  /**
   * Hide/show on scroll.
   *
   * @param  {Object} options.delta The scroll delta
   * @return {void}
   */
  scrolled({ y, delta }) {
    const offset = 200;

    if (!this.isLoaded || y < offset) {
      return;
    }

    let direction = delta.y >= 0 ? 'down' : 'up';
    direction = delta.y === 0 ? 'idle' : direction;

    if (direction === 'down' && y > offset) {
      this.hide();
      this.$children.ProfilTopHeader[0].closeDropdown();
      return;
    }

    if (direction === 'up' || y <= offset) {
      this.show();
    }
  }

  /**
   * Hide the header.
   *
   * @return {Promise} A promise resolving when the hiding transition is done.
   */
  hide() {
    if (!this.isVisible) {
      return Promise.resolve();
    }

    this.$emit('hide');
    this.isVisible = false;

    return transition(
      this.$el,
      {
        active: this.$options.styles.active,
        to: this.$options.styles.hidden,
      },
      'keep'
    );
  }

  /**
   * Show the header.
   *
   * @return {Promise} A promise resolving when the showing transition is done.
   */
  show() {
    if (this.isVisible) {
      return Promise.resolve();
    }

    this.$emit('show');
    this.isVisible = true;
    this.updatePosition();

    return transition(this.$el, {
      from: this.$options.styles.hidden,
      active: this.$options.styles.active,
    });
  }

  /**
   * Init the toolbar behaviours when connected to the back-office.
   * @return {void}
   */
  initToolbarBehaviour() {
    const Drupal = useDrupal();

    if (!Drupal || !Drupal.toolbar) {
      return;
    }

    Drupal.toolbar.models.toolbarModel.on('change:offsets', this.updatePosition);
  }

  /**
   * Update the position of the header when the toolbar is visible.
   * @param  {Number} options.top  The top offset.
   * @param  {Number} options.left The left offset.
   * @return {void}
   */
  updatePosition() {
    const Drupal = useDrupal();

    if (!Drupal || !Drupal.displace) {
      return;
    }

    const { top, left } = Drupal.displace();
    requestAnimationFrame(() => {
      this.$el.style.top = top ? `${top}px` : '';
      this.$el.style.left = left ? `${left}px` : '';
    });
  }
}
